import { useTranslation } from "react-i18next";
import type { ActionKey } from "../toast/ToastProvider";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  Link,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  CUSTOMER_REASON_MAX,
  CUSTOMER_REASON_MIN,
  type PortalCustomerAction,
  type PortalCustomerDetail,
  type PortalCustomerStatus,
  type MinorByCurrency,
} from "@shared/portal-customers";
import EditorCard from "../template/EditorCard";
import { fieldGrid } from "../template/editorLayout";
import { tokens } from "../../theme";
import { friendlyError } from "../../utils/apiError";
import { useFormat, type Formatters } from "../../i18n/format";
import { translateKey, type Translate } from "../../i18n/translate";
import { PermissionGate } from "../../auth/access";
import { describeError, useToast } from "../toast";
import { money } from "../revenue/StatementView";
import { portalCustomersApi } from "../../http/portalCustomers";

/**
 * One portal customer, opened from the staff register: Profile, Devices,
 * Rentals, Orders, Saved titles (the portal's "Min lista") and Log, with an
 * Actions menu. Every open of this drawer is a `tvod.customers.view` row in
 * the audit trail, and every action records the reason typed here.
 */

const EMPTY = "—";
const LOADING = "…";

export const when = (fmt: Formatters, iso: string | null | undefined) =>
  iso ? fmt.dateTime(iso) : EMPTY;

const STATUS_COLOURS: Record<PortalCustomerStatus, { fg: string; bg: string }> =
  {
    active: { fg: tokens.success, bg: tokens.successTint },
    locked: { fg: tokens.warning, bg: tokens.warningTint },
    redacted: { fg: tokens.inkMuted, bg: tokens.rowAlt },
  };

export function StatusChip({ status }: { status: PortalCustomerStatus }) {
  const { t } = useTranslation("offers");
  const v = STATUS_COLOURS[status];
  return (
    <Chip
      size="small"
      label={t(`portalCustomerDrawer.status.${status}`)}
      sx={{ color: v.fg, bgcolor: v.bg, fontWeight: "fontWeightSemibold" }}
      data-testid={`customer-status-${status}`}
    />
  );
}

/** Per currency, side by side - never added together. */
export function moneyByCurrency(
  fmt: Formatters,
  totals: MinorByCurrency
): string {
  const entries = Object.entries(totals).filter(([, v]) => v !== 0);
  if (entries.length === 0) return EMPTY;
  return entries
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([cur, minor]) => money(fmt, minor / 100, cur))
    .join(" · ");
}

const RENTAL_STATES = ["not_started", "active", "expired", "revoked"];
const ORDER_STATES = ["pending", "paid", "failed", "refunded"];

/** A rental or order state as the drawer names it; an unknown one as is. */
function stateLabel(
  t: Translate,
  group: "rental" | "order",
  known: string[],
  state: string
): string {
  return known.includes(state)
    ? translateKey(t, `offers:portalCustomerDrawer.${group}.${state}`)
    : state;
}

/** Which actions destroy; what each is called lives in `actions.<action>`. */
export const DESTRUCTIVE: Record<PortalCustomerAction, boolean> = {
  "resend-confirmation": false,
  "sign-out-everywhere": true,
  lock: true,
  unlock: false,
  export: false,
  erase: true,
};

const MENU_ORDER: PortalCustomerAction[] = [
  "resend-confirmation",
  "sign-out-everywhere",
  "lock",
  "unlock",
  "export",
  "erase",
];

function allowed(d: PortalCustomerDetail, a: PortalCustomerAction): boolean {
  switch (a) {
    case "resend-confirmation":
      return d.actions.resendConfirmation;
    case "sign-out-everywhere":
      return d.actions.signOutEverywhere;
    case "lock":
      return d.actions.lock;
    case "unlock":
      return d.actions.unlock;
    case "export":
      return d.actions.export;
    case "erase":
      return d.actions.erase;
  }
}

function downloadJson(data: unknown, filename: string) {
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function PortalCustomerDrawer({
  customerId,
  onClose,
  onChanged,
}: {
  customerId: string;
  onClose: () => void;
  /** An action changed the customer: the list should refetch. */
  onChanged: () => void;
}) {
  const [detail, setDetail] = useState<PortalCustomerDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [menu, setMenu] = useState<HTMLElement | null>(null);
  const [action, setAction] = useState<PortalCustomerAction | null>(null);
  const toast = useToast();
  const { t } = useTranslation(["offers", "common"]);

  const load = useCallback(async () => {
    try {
      setDetail(await portalCustomersApi.get(customerId));
      setError(null);
    } catch (err) {
      setError(friendlyError(t, err));
    }
  }, [customerId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const p = detail?.profile;

  return (
    <Drawer
      anchor="right"
      open
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: "100%", sm: 640 }, bgcolor: tokens.rowAlt },
      }}
    >
      <Box data-testid="customer-drawer" sx={{ p: 3 }}>
        <Stack direction="row" alignItems="flex-start" spacing={1.5}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="micro"
              sx={{ typography: "eyebrow", color: tokens.inkFaint }}
            >
              {p ? p.portal.name : t("portalCustomerDrawer.customer")}
            </Typography>
            <Typography
              variant="h6"
              sx={{ color: tokens.ink, wordBreak: "break-all" }}
            >
              {p ? p.displayName || p.email : LOADING}
            </Typography>
            {p && p.displayName ? (
              <Typography variant="body2" sx={{ color: tokens.inkMuted }}>
                {p.email}
              </Typography>
            ) : null}
          </Box>
          {p ? <StatusChip status={p.status} /> : null}
          <PermissionGate resource="storefront" level="edit" place="nenda">
            <Button
              variant="outlined"
              size="small"
              endIcon={<ExpandMoreIcon />}
              disabled={!detail}
              onClick={(e) => setMenu(e.currentTarget)}
              data-testid="customer-actions"
            >
              {t("portalCustomerDrawer.actionsMenu")}
            </Button>
          </PermissionGate>
          <IconButton
            aria-label={t("common:buttons.close")}
            onClick={onClose}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Menu
          anchorEl={menu}
          open={!!menu}
          onClose={() => setMenu(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          {detail
            ? MENU_ORDER.flatMap((a) => {
                const item = (
                  <MenuItem
                    key={a}
                    disabled={!allowed(detail, a)}
                    onClick={() => {
                      setMenu(null);
                      setAction(a);
                    }}
                    data-testid={`customer-action-${a}`}
                    sx={DESTRUCTIVE[a] ? { color: tokens.error } : undefined}
                  >
                    <ListItemText>
                      {t(`portalCustomerDrawer.actions.${a}.label`)}
                    </ListItemText>
                  </MenuItem>
                );
                return a === "erase"
                  ? [<Divider key="erase-divider" />, item]
                  : [item];
              })
            : null}
        </Menu>

        <Typography
          variant="caption"
          sx={{ display: "block", mt: 1, mb: 2, color: tokens.inkFaint }}
        >
          {t("portalCustomerDrawer.auditNotice")}
        </Typography>

        {error ? <Alert severity="error">{error}</Alert> : null}
        {!detail && !error ? (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress size={26} />
          </Stack>
        ) : null}
        {detail ? <Sections detail={detail} /> : null}
      </Box>

      {detail && action ? (
        <CustomerActionDialog
          key={action}
          action={action}
          email={detail.profile.email}
          customerId={detail.profile.id}
          onClose={() => setAction(null)}
          onDone={(message) => {
            setAction(null);
            toast.success(message);
            void load();
            onChanged();
          }}
        />
      ) : null}
    </Drawer>
  );
}

function Sections({ detail }: { detail: PortalCustomerDetail }) {
  const { t } = useTranslation("offers");
  const fmt = useFormat();
  const p = detail.profile;
  const f = (key: string) =>
    translateKey(t, `offers:portalCustomerDrawer.facts.${key}`);
  return (
    <Stack spacing={2}>
      <EditorCard
        eyebrow={t("portalCustomerDrawer.profileEyebrow")}
        data-testid="customer-profile"
      >
        <Box sx={fieldGrid(200)}>
          <Fact label={f("portal")}>{p.portal.name}</Fact>
          <Fact label={f("type")}>
            {p.kind === "member"
              ? t("portalCustomerDrawer.facts.memberOf", {
                  organisation: p.organisation?.name || f("anOrganisation"),
                })
              : f("privateCustomer")}
          </Fact>
          <Fact label={f("emailConfirmed")}>
            {when(fmt, p.emailVerifiedAt)}
          </Fact>
          <Fact label={f("registered")}>{when(fmt, p.createdAt)}</Fact>
          <Fact label={f("lastSignIn")}>{when(fmt, p.lastLoginAt)}</Fact>
          <Fact label={f("language")}>{p.locale ?? f("portalDefault")}</Fact>
          <Fact label={f("marketing")}>
            {p.marketingOptIn ? f("optedIn") : f("notOptedIn")}
          </Fact>
          <Fact label={f("paid")}>{moneyByCurrency(fmt, p.paidTotal)}</Fact>
          <Fact label={f("password")}>
            {p.hasPassword ? f("passwordSet") : f("passwordNone")}
          </Fact>
          <Fact label={f("wrongPasswords")}>
            {p.failedLogins}
            {p.loginLockedUntil
              ? t("portalCustomerDrawer.facts.signInPaused", {
                  when: when(fmt, p.loginLockedUntil),
                })
              : ""}
          </Fact>
          {p.redactedAt ? (
            <Fact label={f("erased")}>{when(fmt, p.redactedAt)}</Fact>
          ) : null}
          <Fact label={f("accountId")}>
            <Box
              component="span"
              sx={{ fontFamily: "fontFamilyMono", typography: "meta" }}
            >
              {p.id}
            </Box>
          </Fact>
        </Box>
      </EditorCard>

      <EditorCard
        eyebrow={t("portalCustomerDrawer.devicesEyebrow")}
        action={
          <Count n={detail.sessions.filter((s) => !s.revokedAt).length} />
        }
        hint={t("portalCustomerDrawer.devicesHint")}
      >
        <SmallTable
          testId="customer-sessions"
          empty={t("portalCustomerDrawer.neverSignedIn")}
          head={[
            t("portalCustomerDrawer.deviceColumns.device"),
            t("portalCustomerDrawer.deviceColumns.kind"),
            t("portalCustomerDrawer.deviceColumns.signedIn"),
            t("portalCustomerDrawer.deviceColumns.lastUsed"),
            t("portalCustomerDrawer.deviceColumns.ended"),
          ]}
          rows={detail.sessions.map((s) => [
            s.deviceLabel || t("portalCustomerDrawer.unnamedDevice"),
            s.kind === "app"
              ? t("portalCustomerDrawer.kindApp")
              : t("portalCustomerDrawer.kindWeb"),
            when(fmt, s.createdAt),
            when(fmt, s.lastUsedAt),
            s.revokedAt
              ? s.revokedReason
                ? t("portalCustomerDrawer.endedWithReason", {
                    when: when(fmt, s.revokedAt),
                    reason: s.revokedReason.replace(/_/g, " "),
                  })
                : when(fmt, s.revokedAt)
              : EMPTY,
          ])}
        />
      </EditorCard>

      <EditorCard
        eyebrow={t("portalCustomerDrawer.rentalsEyebrow")}
        action={<Count n={detail.rentals.length} />}
        hint={t("portalCustomerDrawer.rentalsHint")}
      >
        <SmallTable
          testId="customer-rentals"
          empty={t("portalCustomerDrawer.noRentals")}
          head={[
            t("portalCustomerDrawer.rentalColumns.title"),
            t("portalCustomerDrawer.rentalColumns.state"),
            t("portalCustomerDrawer.rentalColumns.startBy"),
            t("portalCustomerDrawer.rentalColumns.started"),
            t("portalCustomerDrawer.rentalColumns.ends"),
          ]}
          rows={detail.rentals.map((r) => [
            <TitleCell key="t" title={r.title} assetId={r.assetId} />,
            stateLabel(t, "rental", RENTAL_STATES, r.state),
            when(fmt, r.startBy),
            when(fmt, r.startedAt),
            when(fmt, r.endsAt),
          ])}
        />
      </EditorCard>

      <EditorCard
        eyebrow={t("portalCustomerDrawer.ordersEyebrow")}
        action={<Count n={detail.orders.length} />}
        hint={t("portalCustomerDrawer.ordersHint")}
      >
        <SmallTable
          testId="customer-orders"
          empty={t("portalCustomerDrawer.noOrders")}
          head={[
            t("portalCustomerDrawer.orderColumns.order"),
            t("portalCustomerDrawer.orderColumns.state"),
            t("portalCustomerDrawer.orderColumns.amount"),
            t("portalCustomerDrawer.orderColumns.paid"),
            t("portalCustomerDrawer.orderColumns.refunded"),
          ]}
          rows={detail.orders.map((o) => [
            <Link
              key="ref"
              href={`/distribution/order-support?ref=${encodeURIComponent(o.orderRef)}`}
              sx={{ fontFamily: "fontFamilyMono", typography: "meta" }}
              data-testid={`customer-order-${o.orderRef}`}
            >
              {o.orderRef}
            </Link>,
            stateLabel(t, "order", ORDER_STATES, o.state),
            money(fmt, o.amountMinor / 100, o.currency),
            when(fmt, o.paidAt),
            when(fmt, o.refundedAt),
          ])}
        />
      </EditorCard>

      <EditorCard
        eyebrow={t("portalCustomerDrawer.watchlistEyebrow")}
        action={<Count n={detail.watchlist.length} />}
      >
        <SmallTable
          testId="customer-watchlist"
          empty={t("portalCustomerDrawer.nothingSaved")}
          head={[
            t("portalCustomerDrawer.watchlistColumns.title"),
            t("portalCustomerDrawer.watchlistColumns.saved"),
          ]}
          rows={detail.watchlist.map((w) => [
            <TitleCell key="t" title={w.title} assetId={w.assetId} />,
            when(fmt, w.savedAt),
          ])}
        />
      </EditorCard>

      <EditorCard
        eyebrow={t("portalCustomerDrawer.logEyebrow")}
        hint={t("portalCustomerDrawer.logHint")}
      >
        <SmallTable
          testId="customer-log"
          empty={t("portalCustomerDrawer.nothingRecorded")}
          head={[
            t("portalCustomerDrawer.logColumns.when"),
            t("portalCustomerDrawer.logColumns.event"),
            t("portalCustomerDrawer.logColumns.by"),
            t("portalCustomerDrawer.logColumns.reason"),
          ]}
          rows={detail.audit.map((a) => [
            when(fmt, a.at),
            <span key="e">
              {a.action}
              {a.outcome !== "success" ? (
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ color: tokens.error, ml: 0.75 }}
                >
                  {a.outcome}
                </Typography>
              ) : null}
            </span>,
            a.actor.startsWith("consumer:")
              ? t("portalCustomerDrawer.actorCustomer")
              : a.actor,
            a.reason ?? EMPTY,
          ])}
        />
      </EditorCard>
    </Stack>
  );
}

function Count({ n }: { n: number }) {
  return (
    <Typography variant="caption" sx={{ color: tokens.inkMuted }}>
      {n}
    </Typography>
  );
}

function TitleCell({
  title,
  assetId,
}: {
  title: string | null;
  assetId: string;
}) {
  return title ? (
    <Box>
      {title}
      <Typography
        variant="caption"
        sx={{
          display: "block",
          color: tokens.inkFaint,
          fontFamily: "fontFamilyMono",
        }}
      >
        {assetId}
      </Typography>
    </Box>
  ) : (
    <Box
      component="span"
      sx={{ fontFamily: "fontFamilyMono", typography: "meta" }}
    >
      {assetId}
    </Box>
  );
}

function SmallTable({
  head,
  rows,
  empty,
  testId,
}: {
  head: string[];
  rows: ReactNode[][];
  empty: string;
  testId: string;
}) {
  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" data-testid={testId}>
        {empty}
      </Typography>
    );
  }
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small" data-testid={testId}>
        <TableHead>
          <TableRow>
            {head.map((h) => (
              <TableCell key={h}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((cells, i) => (
            <TableRow key={i}>
              {cells.map((c, j) => (
                <TableCell key={j}>{c}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box>
      <Typography variant="caption" sx={{ color: tokens.inkFaint }}>
        {label}
      </Typography>
      <Typography variant="body2" component="div" sx={{ color: tokens.ink }}>
        {children}
      </Typography>
    </Box>
  );
}

const FAILED: Record<PortalCustomerAction, ActionKey> = {
  "resend-confirmation": "resendCustomerConfirmation",
  "sign-out-everywhere": "signOutCustomerEverywhere",
  lock: "lockCustomerAccount",
  unlock: "unlockCustomerAccount",
  export: "exportCustomerData",
  erase: "eraseCustomer",
};

/**
 * Every action asks WHY - at least five characters, recorded in the audit
 * trail. Erase also asks for the account's email typed back, so a slip of
 * the mouse cannot erase the wrong person.
 */
export function CustomerActionDialog({
  action,
  email,
  customerId,
  onClose,
  onDone,
}: {
  action: PortalCustomerAction;
  email: string;
  customerId: string;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const toast = useToast();
  const { t } = useTranslation(["offers", "common"]);
  const spec = {
    label: t(`portalCustomerDrawer.actions.${action}.label`),
    body: t(`portalCustomerDrawer.actions.${action}.body`),
    confirm: t(`portalCustomerDrawer.actions.${action}.confirm`),
    done: t(`portalCustomerDrawer.actions.${action}.done`),
    destructive: DESTRUCTIVE[action],
  };
  const [reason, setReason] = useState("");
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reasonOk = reason.trim().length >= CUSTOMER_REASON_MIN;
  const emailOk =
    action !== "erase" ||
    typed.trim().toLowerCase() === email.trim().toLowerCase();

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await portalCustomersApi.act(customerId, action, {
        reason: reason.trim(),
        ...(action === "erase" ? { confirmEmail: typed.trim() } : {}),
      });
      if (action === "export") {
        downloadJson(result, `customer-${customerId}.json`);
      }
      onDone(
        t("portalCustomerDrawer.doneToast", { email, message: spec.done })
      );
    } catch (err) {
      setError(friendlyError(t, err));
      toast.error(describeError(t, FAILED[action], err, { email }));
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      data-testid="customer-action-dialog"
    >
      <DialogTitle sx={spec.destructive ? { color: tokens.error } : undefined}>
        {spec.label}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" sx={{ color: tokens.inkMuted }}>
            {spec.body}
          </Typography>
          <TextField
            label={t("portalCustomerDrawer.reasonLabel")}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            minRows={2}
            required
            helperText={t("portalCustomerDrawer.reasonHelp", {
              count: CUSTOMER_REASON_MIN,
            })}
            inputProps={{
              maxLength: CUSTOMER_REASON_MAX,
              "data-testid": "customer-reason",
            }}
          />
          {action === "erase" ? (
            <TextField
              label={t("portalCustomerDrawer.confirmEmail")}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              required
              helperText={email}
              inputProps={{
                spellCheck: false,
                autoComplete: "off",
                "data-testid": "customer-confirm-email",
              }}
            />
          ) : null}
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t("common:buttons.cancel")}
        </Button>
        <Button
          variant="contained"
          color={spec.destructive ? "error" : "primary"}
          disabled={busy || !reasonOk || !emailOk}
          onClick={() => void submit()}
          data-testid="customer-action-confirm"
        >
          {busy ? t("portalCustomerDrawer.working") : spec.confirm}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
